import {flatten, partition} from 'lodash';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {
  ConflictingMergeCardValues,
  ContactDetailsConflictingFieldKeysEnum,
  ContactDetailsSectionWithConflictsEnum,
} from '../../../helpers/models/cardMergeHelpers';
import {OverrideMergeValues} from '../../../store/data/cards/cardsActions';
import {DropdownCategory} from '../../common/dropdowns/dropdownCategory';
import {useContactDetailsContext} from '../contactDetailsContext';

/*
 * Constants.
 */

const minDropdownWidth = 250;

/*
 * Props.
 */

interface ContactMergeConflictingValueProps {
  section: ContactDetailsSectionWithConflictsEnum;
  sectionKey?: ContactDetailsConflictingFieldKeysEnum;
  customFieldId?: number;
  shouldRemoveTopAndBottomPadding?: boolean;

  // A custom renderer for the content of the option
  renderOption?: RendererOf<string | number>;

  // If you wrap ContactMergeConflictingValue in a styled component, we will use
  // this value to style the currently selected option.
  className?: string;
}

interface StyledButtonContentDivProps {
  $isPopoverOpen: boolean;
  $shouldRemoveTopAndBottomPadding: boolean;
}

interface StyledDropdownItemProps {
  $isSelected: boolean;
}

/*
 * Styles.
 */

const StyledConflictingValueContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex: 1;
  overflow: hidden;
`;

const StyledDropdownContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;

  & > div[role='button'] {
    display: inline-block;
    overflow: hidden;
    max-width: 100%;
  }
`;

const StyledButtonContentDiv = styled.div<StyledButtonContentDivProps>`
  display: flex;
  flex-flow: row;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 7px 8px;

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
  }
  ${(p) =>
    p.$isPopoverOpen &&
    css`
      background: ${p.theme.greys.shade30};
    `}
  ${(p) =>
    p.$shouldRemoveTopAndBottomPadding &&
    css`
      padding-top: 0;
      padding-bottom: 0;
    `}
`;

const StyledSelectedValueDiv = styled.div`
  flex: 1;
  ${ellipsis()}
`;

const StyledIconContainerDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  padding-left: 8px;
`;

const StyledConflictIndicatorDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 5px 0 16px;
`;

const StyledTooltipTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
`;

const StyledDropdownItem = styled(DropdownItem)<StyledDropdownItemProps>`
  ${(p) =>
    p.$isSelected &&
    css`
      font-weight: ${fontWeights.bold};
    `}
`;

/*
 * Component.
 */

export const ContactMergeConflictingValue: FC<ContactMergeConflictingValueProps> = (props) => {
  const theme = useUserTheme();
  const {section, sectionKey, customFieldId, className, shouldRemoveTopAndBottomPadding, renderOption} =
    props;
  const [selectorButtonRef, {width: selectorButtonWidth}] = useMeasureElement();
  const contactDetailsContext = useContactDetailsContext();
  const dropdownWidth = selectorButtonWidth > minDropdownWidth ? selectorButtonWidth : minDropdownWidth;

  // Get the selected value but also check if we have an overrode value selected.
  const conflictingMergeDetails = getSelectedValue(
    contactDetailsContext.mergeContext.conflictingMergeValues,
    section,
    sectionKey,
    customFieldId,
  );
  const overriddenValue = getOverriddenSelectedValue(
    contactDetailsContext.mergeContext.overrideMergeValues,
    section,
    sectionKey,
    customFieldId,
  );
  const selectedValue = overriddenValue ?? conflictingMergeDetails?.selectedValue;

  const onValueSelect = (value: string | number) => {
    if (section === ContactDetailsSectionWithConflictsEnum.HEADER && sectionKey) {
      if (sectionKey === ContactDetailsConflictingFieldKeysEnum.ACCOUNT) {
        contactDetailsContext.mergeContext.setMergeOverrides({section, sectionKey, accountId: Number(value)});
      } else if (sectionKey === ContactDetailsConflictingFieldKeysEnum.NAME) {
        contactDetailsContext.mergeContext.setMergeOverrides({section, sectionKey, name: `${value}`});
      }
    } else if (section === ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION && customFieldId) {
      contactDetailsContext.mergeContext.setMergeOverrides({
        section,
        sectionKey,
        customFieldId,
        customFieldAttribute: `${value}`,
      });
    }
  };

  if (!conflictingMergeDetails) {
    return null;
  }
  return (
    <StyledConflictingValueContainerDiv>
      <StyledDropdownContainerDiv>
        <PopoverCoordinator
          renderButton={(isOpen) => (
            <StyledButtonContentDiv
              $isPopoverOpen={isOpen}
              $shouldRemoveTopAndBottomPadding={Boolean(shouldRemoveTopAndBottomPadding)}
              ref={selectorButtonRef}
            >
              <StyledSelectedValueDiv className={className}>
                {renderOption && selectedValue ? renderOption(selectedValue) : selectedValue}
              </StyledSelectedValueDiv>
              <StyledIconContainerDiv>
                <Icon name="chevronDownSmall" color={theme.greys.shade60} />
              </StyledIconContainerDiv>
            </StyledButtonContentDiv>
          )}
          renderModal={(popoverProps) => (
            <Dropdown {...popoverProps} placement="bottom-start" width={dropdownWidth}>
              <DropdownList {...popoverProps}>
                <DropdownCategory>
                  <Trans id="contact-details-merge-conflict-swap-value">Swap value</Trans>
                </DropdownCategory>
                {flatten(
                  partition<string | number>(
                    conflictingMergeDetails.availableValues,
                    (value) => value === selectedValue,
                  ),
                ).map((value) => {
                  const isValueSelected = value === selectedValue;
                  return (
                    <StyledDropdownItem
                      key={value}
                      $isSelected={isValueSelected}
                      onSelect={() => onValueSelect(value)}
                      isChecked={isValueSelected}
                    >
                      <DropdownItemContent>{renderOption ? renderOption(value) : value}</DropdownItemContent>
                    </StyledDropdownItem>
                  );
                })}
              </DropdownList>
            </Dropdown>
          )}
        />
      </StyledDropdownContainerDiv>
      {renderConflictIndicator()}
    </StyledConflictingValueContainerDiv>
  );
};

const renderConflictIndicator = () => (
  <UserThemeConsumer>
    {(theme) => (
      <TooltipCoordinator
        render={() => (
          <>
            <StyledTooltipTitleDiv>
              <Trans id="contact-details-merge-conflict-tooltip-title">Conflicting values</Trans>
            </StyledTooltipTitleDiv>
            <div>
              <Trans id="contact-details-merge-conflict-tooltip-body">We picked the most recent one</Trans>
            </div>
          </>
        )}
        placement="top"
        renderTarget={() => (
          <StyledConflictIndicatorDiv>
            <Icon
              name="warningFilledSmall"
              color={theme.palette.yellow.shade40}
              hoverColor={theme.palette.yellow.shade50}
            />
          </StyledConflictIndicatorDiv>
        )}
      />
    )}
  </UserThemeConsumer>
);

/*
 * Helpers.
 */

function getSelectedValue(
  conflictingValues: ConflictingMergeCardValues | undefined,
  section: ContactDetailsSectionWithConflictsEnum,
  key?: ContactDetailsConflictingFieldKeysEnum,
  id?: number,
) {
  if (!conflictingValues) {
    return null;
  }
  if (key && section === ContactDetailsSectionWithConflictsEnum.HEADER) {
    return conflictingValues[section]?.find((field) => field.key === key);
  }
  if (id && section === ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION) {
    return conflictingValues[section]?.find((field) => field.id === id);
  }
  return null;
}

function getOverriddenSelectedValue(
  overrideMergeValues: OverrideMergeValues | undefined,
  section: ContactDetailsSectionWithConflictsEnum,
  key?: ContactDetailsConflictingFieldKeysEnum,
  id?: number,
) {
  if (!overrideMergeValues) {
    return undefined;
  }
  if (section === ContactDetailsSectionWithConflictsEnum.HEADER) {
    if (key === ContactDetailsConflictingFieldKeysEnum.NAME && overrideMergeValues.name) {
      return overrideMergeValues.name;
    }
    if (key === ContactDetailsConflictingFieldKeysEnum.ACCOUNT && overrideMergeValues.accountId) {
      return overrideMergeValues.accountId;
    }
  }
  if (id && overrideMergeValues.customFieldAttributes) {
    return overrideMergeValues.customFieldAttributes[id];
  }

  return undefined;
}
